import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, type ReactPortal, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { LAYER_NEST_STEP, Z_INDEX } from '../../constants/zIndex';
import { LayerBoundary, useLayerZIndex } from '../../providers/LayerProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Button } from '../forms/Button';

/**
 * The z-index a confirm renders at when nothing encloses it: one nesting step
 * above the dialog tier (the shared Modal and Sidebar, 50), so a confirm opened
 * from inside a plain Headless UI dialog that declares no layer of its own (the
 * list-configuration delete, PD-13609) still renders above it.
 */
export const CONFIRM_DIALOG_Z_INDEX = Z_INDEX.dialog + LAYER_NEST_STEP;

export interface ConfirmDialogProps {
	isOpen: boolean;
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	variant?: 'danger' | 'warning' | 'info';
	/** When true, the confirm button is hidden (e.g., informational-only dialogs) */
	hideConfirmButton?: boolean;
	/**
	 * When true, disables both buttons and prevents backdrop dismissal —
	 * indicates the confirm action is in flight.
	 */
	isProcessing?: boolean;
	/**
	 * Optional content under the message, for a confirmation that carries
	 * one small choice (e.g. which channels to send a message by).
	 */
	children?: ReactNode;
	/** When true, the confirm button is disabled (e.g. nothing chosen yet). */
	confirmDisabled?: boolean;
}

export const ConfirmDialog = ({
	isOpen,
	title,
	message,
	confirmText,
	cancelText,
	onConfirm,
	onCancel,
	variant = 'danger',
	hideConfirmButton = false,
	isProcessing = false,
	children,
	confirmDisabled = false,
}: ConfirmDialogProps): ReactPortal | null => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const titleId = useId();
	// PD-15134: the layer this confirm sits on. At the top level it is
	// CONFIRM_DIALOG_Z_INDEX (60); opened from inside an overlay that declares
	// its layer (a Modal, a Sidebar, a floating chat or email window at 2001+)
	// it rises above that overlay. `useLayerZIndex` never returns less than the
	// value it is given, so 60 stays the floor: a fixed `z-[60]` sat underneath
	// the floating windows, and a confirm opened from one was hidden behind it.
	const zIndex = useLayerZIndex(CONFIRM_DIALOG_Z_INDEX);

	// Escape cancels, like every other dialog. The listener is on the document
	// so it runs before Headless UI's own Escape handler on the window, and it
	// marks the event handled: Headless UI ignores a default-prevented Escape,
	// so the dialog this confirm was opened from stays open. While the confirm
	// action is in flight Escape does nothing, matching the backdrop.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key !== 'Escape' || event.defaultPrevented) {
				return;
			}
			event.preventDefault();
			if (!isProcessing) {
				onCancel();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, isProcessing, onCancel]);

	if (!isOpen) {
		return null;
	}

	// The confirm button colour follows the dialog variant so this generic
	// dialog isn't always red: destructive ('danger') stays red, while
	// non-destructive confirmations ('info'/'warning') use the primary button.
	// `variant` defaults to 'danger', so existing delete dialogs are unchanged.
	const confirmButtonVariant = variant === 'danger' ? 'danger' : 'primary';
	const defaultConfirmText = variant === 'danger' ? t('Delete') : t('Confirm');

	// Portal to document.body so this stacks above any Headless UI Dialog that
	// opened it (PD-13609). Without the portal, the dialog renders inside the
	// parent modal's portal/stacking context and z-index can't escape. A direct
	// child of <body> also counts as one of Headless UI's root containers, so a
	// click on the confirm is not an outside click that closes that dialog.
	return createPortal(
		<div
			className="fixed inset-0 flex items-center justify-center"
			style={{ zIndex }}
		>
			<div
				className={cn('absolute inset-0', colors.classes.backdropOverlay)}
				onClick={isProcessing ? undefined : onCancel}
			/>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className={cn(
					'relative rounded-lg shadow-2xl w-full max-w-md m-4',
					'border border-default',
					'ring-1 ring-black ring-opacity-5',
					colors.classes.bgPrimary,
				)}
			>
				<div className={cn('px-6 py-4 border-b', colors.classes.borderDefault)}>
					<div className="flex items-center gap-3">
						<FontAwesomeIcon
							icon={faExclamationTriangle}
							className={cn(
								'h-6 w-6',
								variant === 'danger' && 'text-danger',
								variant === 'warning' && 'text-warning',
								variant === 'info' && 'text-accent',
							)}
						/>
						<h2
							id={titleId}
							className={cn(
								'text-xl font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{title}
						</h2>
					</div>
				</div>

				<div className="px-6 py-4">
					<p className={cn('text-sm', colors.classes.textSecondary)}>
						{message}
					</p>
					{children != null && (
						<div className="mt-3">
							{/* A select or menu in the extra content opens above the confirm. */}
							<LayerBoundary zIndex={zIndex}>{children}</LayerBoundary>
						</div>
					)}
				</div>

				<div
					className={cn(
						'px-6 py-4 border-t flex justify-end gap-3',
						colors.classes.borderDefault,
					)}
				>
					<Button variant="outline" onClick={onCancel} disabled={isProcessing}>
						{cancelText ?? t('Cancel')}
					</Button>
					{!hideConfirmButton && (
						<Button
							variant={confirmButtonVariant}
							onClick={onConfirm}
							disabled={isProcessing || confirmDisabled}
						>
							{confirmText ?? defaultConfirmText}
						</Button>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
